export interface ZoomRange {
  start: number
  end: number
}

export interface ZoomHistory {
  entries: ZoomRange[]
  index: number
}

const FULL_RANGE: ZoomRange = { start: 0, end: 100 }

export function normalizeZoomRange(range: ZoomRange): ZoomRange {
  const start = Number.isFinite(range.start) ? Math.max(0, Math.min(100, range.start)) : 0
  const end = Number.isFinite(range.end) ? Math.max(0, Math.min(100, range.end)) : 100
  return start <= end ? { start, end } : { start: end, end: start }
}

export function sameZoomRange(left: ZoomRange, right: ZoomRange, tolerance = 0.01): boolean {
  return Math.abs(left.start - right.start) <= tolerance && Math.abs(left.end - right.end) <= tolerance
}

export function createZoomHistory(): ZoomHistory {
  return { entries: [{ ...FULL_RANGE }], index: 0 }
}

export function appendZoomHistory(history: ZoomHistory, range: ZoomRange): ZoomHistory {
  const normalized = normalizeZoomRange(range)
  const current = history.entries[history.index] ?? FULL_RANGE
  if (sameZoomRange(current, normalized)) return history
  return { entries: [...history.entries.slice(0, history.index + 1), normalized], index: history.index + 1 }
}

export function navigateZoomHistory(history: ZoomHistory, delta: -1 | 1): ZoomHistory {
  return { ...history, index: Math.max(0, Math.min(history.entries.length - 1, history.index + delta)) }
}

export function zoomOutRange(range: ZoomRange, factor = 1.6): ZoomRange {
  const normalized = normalizeZoomRange(range)
  const span = normalized.end - normalized.start
  if (span <= 0 || span >= 100) return { ...FULL_RANGE }
  const expanded = Math.min(100, span * Math.max(1, factor))
  const center = (normalized.start + normalized.end) / 2
  let start = center - expanded / 2
  let end = center + expanded / 2
  if (start < 0) {
    end -= start
    start = 0
  }
  if (end > 100) {
    start -= end - 100
    end = 100
  }
  return normalizeZoomRange({ start, end })
}

export function isFullZoomRange(range: ZoomRange): boolean {
  return sameZoomRange(normalizeZoomRange(range), FULL_RANGE)
}
